import { Injectable } from "@angular/core";
import { IktvaForm } from "../models/iktvaForm.model";
import { EMPTY, Observable, from } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ContactUs } from "../models/contactUs.model";

const CHUNK_SIZE = 100 * 1024; // 100 KB
const MAX_RETRIES = 3;

export interface ChunkUploadResponse {
  success: boolean;
  fileName: string;
  message: string;
}

@Injectable({ providedIn: "root" })
export class IktvaFormService {
  constructor(private http: HttpClient) {}

  public submitForm(form: IktvaForm): Observable<IktvaForm> {
    return this.http.post<IktvaForm>('api/iktvaform', form);
  }

  public fetchForm(formId: string): Observable<IktvaForm> {
    if (formId && formId !== '')
      return this.http.get<IktvaForm>('/api/iktvaform/' + formId);
    return EMPTY;
  }

  uploadManufacturingFiles(iktvaUniqueId: string, files: File[], onProgress?: (pct: number) => void): Observable<boolean> {
    return from(this.uploadFilesChunked('/api/manufacturingfile', iktvaUniqueId, files, onProgress));
  }

  uploadQualityAuditFiles(iktvaUniqueId: string, files: File[], onProgress?: (pct: number) => void): Observable<boolean> {
    return from(this.uploadFilesChunked('/api/qualityauditfile', iktvaUniqueId, files, onProgress));
  }

  uploadIktvaPlanFiles(iktvaUniqueId: string, files: File[], onProgress?: (pct: number) => void): Observable<boolean> {
    return from(this.uploadFilesChunked('/api/iktvaplanfile', iktvaUniqueId, files, onProgress));
  }

  private async uploadFilesChunked(
    endpoint: string,
    iktvaUniqueId: string,
    files: File[],
    onProgress?: (pct: number) => void
  ): Promise<boolean> {
    if (!files || files.length === 0) return true;

    const totalChunks = files.reduce((sum, f) => sum + Math.ceil(f.size / CHUNK_SIZE) || 1, 0);
    let completedChunks = 0;

    for (const file of files) {
      const uploadId = this.generateGuid();
      const chunkCount = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

      for (let i = 1; i <= chunkCount; i++) {
        const start = (i - 1) * CHUNK_SIZE;
        const end = Math.min(i * CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('fileUpload', chunk, file.name);
        formData.append('fileName', file.name);
        formData.append('currentChunk', i.toString());
        formData.append('chunkCount', chunkCount.toString());
        formData.append('uploadId', uploadId);
        formData.append('iktvaUniqueId', iktvaUniqueId);

        await this.postChunkWithRetry(endpoint, formData);

        completedChunks++;
        if (onProgress) {
          onProgress(Math.round((completedChunks / totalChunks) * 100));
        }
      }
    }
    return true;
  }

  private postChunkWithRetry(endpoint: string, formData: FormData): Promise<ChunkUploadResponse> {
    return new Promise<ChunkUploadResponse>((resolve, reject) => {
      let attempt = 0;
      const tryPost = () => {
        attempt++;
        this.http.post<ChunkUploadResponse>(endpoint, formData).subscribe({
          next: (res: ChunkUploadResponse) => resolve(res),
          error: (err: unknown) => {
            if (attempt < MAX_RETRIES) {
              setTimeout(tryPost, 500 * attempt);
            } else {
              reject(err);
            }
          }
        });
      };
      tryPost();
    });
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  updateForm(form: IktvaForm): Observable<IktvaForm> {
    return this.http.put<IktvaForm>('api/iktvaform/' + form.UniqueIktvaId, form);
  }

  submitContactUs(form: ContactUs): Observable<boolean>{
    return this.http.post<boolean>('api/contactus', form);
  }

  downloadFile(fileId: string): Observable<any> {
    return this.http.get('/api/download/' + fileId, { responseType: 'blob' as 'json' });
  }
}
